import React, { FC, useState, useEffect, useCallback, ChangeEvent } from 'react'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import { FirebaseTimestamp } from 'firebase/index'
import { State } from 'index'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { addMonths, endOfDay, endOfToday, format, startOfDay } from 'date-fns'
import { PointCardUserList } from 'components/List'
import { PointCardUserPointType } from 'types/pointCard/pointCardUserPoint'
import { Spacer } from 'components/Utility'
import { Description } from 'components/Typography'
import { FlexRow } from 'components/Flex'
import { DatePicker } from 'components/Form'
import { NavButton } from 'components/Button'
import { Pagination } from 'components/Pagination'
import { PER_PAGE } from 'constants/pointCard'
import { getPointCard } from 'reducks/pointCard/selectors'
import { getStoreCreatedAt } from 'reducks/store/selectors'
import { fetchPointCardUserPointsByPointCardRef } from 'repos/pointCard/pointCardUserPoint'
import classNames from 'classnames'

const PointCardUserTemplate: FC = () => {
  const dispatch = useDispatch()
  const pointCard = useSelector((state: State) => getPointCard(state))
  const storeCreatedAt = useSelector((state: State) => getStoreCreatedAt(state))
  const pathname = useSelector((state: State) => state.router.location.pathname)
  const query = useSelector((state: State) => state.router.location.query)
  const currentPage = Number(query?.page ?? '1')

  const [pointCardUserPoints, setPointCardUserPoints] = useState<PointCardUserPointType[]>([])
  const [startOfPeriod, setStartOfPeriod] = useState<string>(format(addMonths(new Date(), -1), 'yyyy-MM-dd'))
  const [endOfPeriod, setEndOfPeriod] = useState<string>(format(new Date(), 'yyyy-MM-dd'))

  const initPage = () => {
    dispatch(push(pathname))
  }

  const handleStartOfPeriod = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value !== '' && endOfPeriod !== '' && e.target.value > endOfPeriod) {
        return
      }
      setStartOfPeriod(e.target.value)
      initPage()
    },
    [setStartOfPeriod, endOfPeriod, initPage]
  )

  const handleEndOfPeriod = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value !== '' && startOfPeriod !== '' && e.target.value < startOfPeriod) {
        return
      }
      setEndOfPeriod(e.target.value)
      initPage()
    },
    [setEndOfPeriod, startOfPeriod, initPage]
  )

  useEffect(() => {
    ;(async () => {
      if (!pointCard) return
      const pointCardRef = pointCard.ref
      const startAt = startOfPeriod ? FirebaseTimestamp.fromDate(startOfDay(new Date(startOfPeriod))) : storeCreatedAt
      const endAt = endOfPeriod
        ? FirebaseTimestamp.fromDate(endOfDay(new Date(endOfPeriod)))
        : FirebaseTimestamp.fromDate(endOfToday())
      const pointCardUserPoints = await fetchPointCardUserPointsByPointCardRef(pointCardRef, startAt, endAt)
      setPointCardUserPoints(pointCardUserPoints)
    })()
  }, [pointCard, startOfPeriod, endOfPeriod])

  const totalPointCardUserPoints = pointCardUserPoints.length ?? 0

  const startIndex = (currentPage - 1) * PER_PAGE
  const endIndex = startIndex + PER_PAGE
  const currentData = pointCardUserPoints.slice(startIndex, endIndex).length ?? 0

  return (
    <div className={styles.standard}>
      <Spacer size={40} />
      <Description description={`${totalPointCardUserPoints}件中${currentData}件を表示中`} />
      <Spacer size={12} />
      <FlexRow justifyContent={'start'}>
        <label className={classNames(text.font_small)}>ポイント付与履歴</label>
        <Spacer size={16} axis={'horizontal'} />
        <DatePicker
          formSize={'xsmall'}
          max={endOfPeriod || format(new Date(), 'yyyy-MM-dd')}
          onChange={handleStartOfPeriod}
          value={startOfPeriod}
        />
        <Spacer size={16} axis={'horizontal'} />
        <span>〜</span>
        <Spacer size={16} axis={'horizontal'} />
        <DatePicker
          formSize={'xsmall'}
          max={format(new Date(), 'yyyy-MM-dd')}
          min={startOfPeriod || undefined}
          onChange={handleEndOfPeriod}
          value={endOfPeriod}
        />
      </FlexRow>
      <Spacer size={20} />
      <PointCardUserList pointCardUserPoints={pointCardUserPoints} startIndex={startIndex} endIndex={endIndex} />
      <Spacer size={24} />
      <Pagination perPage={PER_PAGE} total={totalPointCardUserPoints} />
      <Spacer size={24} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={40} />
    </div>
  )
}

export default PointCardUserTemplate
